import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, DollarSign, CreditCard, Smartphone, Building2, Banknote, MoreVertical, Download, XCircle, Clock, Calendar, Eye, Edit2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { paymentsReceivedAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PAYMENT_MODE_CONFIG = {
  cash: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  upi: { icon: Smartphone, label: 'UPI', bg: 'bg-purple-100', text: 'text-purple-700' },
  bank_transfer: { icon: Building2, label: 'Bank', bg: 'bg-blue-100', text: 'text-blue-700' },
  card: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  cheque: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  wallet: { icon: DollarSign, label: 'Wallet', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  other: { icon: DollarSign, label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700' }
};

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
  voided: { bg: 'bg-red-100', text: 'text-red-700', label: 'Voided' },
  refunded: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Refunded' }
};

const PAYMENT_TYPE_CONFIG = {
  full: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Full Payment' },
  partial: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partial' },
  advance: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Advance' }
};

const getReceiptNo = (p) => p.receipt_no || p.receipt_number || '';
const getCustomerName = (p) => p.customer_name || p.party_name || '';
const getPaymentType = (payment) => {
  const allocs = payment.allocations || payment.invoices_allocated || [];
  if (!allocs || allocs.length === 0) return 'advance';
  if ((payment.unallocated_amount || payment.excess_amount || 0) > 0) return 'partial';
  return 'full';
};

const PaymentRow = ({ payment, index, onView, onEdit, onVoid, onDelete, onDownload, bid, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const modeConfig = PAYMENT_MODE_CONFIG[payment.payment_mode] || PAYMENT_MODE_CONFIG.other;
  const statusConfig = STATUS_CONFIG[payment.status] || STATUS_CONFIG.draft;
  const paymentType = getPaymentType(payment);
  const typeConfig = PAYMENT_TYPE_CONFIG[paymentType];
  const ModeIcon = modeConfig.icon;
  const pendingAmount = payment.unallocated_amount || payment.excess_amount || 0;
  const allocs = payment.allocations || payment.invoices_allocated || [];
  const invoiceRefs = Array.isArray(allocs) ? allocs.map(a => a.invoice_number).filter(Boolean).join(', ') : '';

  return (
    <>
      <tr
        className={`tl-tr tl-tr-clickable hover:bg-emerald-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="tl-td" style={{ width: '3%' }}>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </td>
        <td className="tl-td" style={{ width: '10%' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[12px] font-medium text-emerald-700">{getReceiptNo(payment).slice(-2) || 'RC'}</span>
            </div>
            <span className="tl-cell-primary">{getReceiptNo(payment)}</span>
          </div>
        </td>
        <td className="tl-td tl-cell-regular" style={{ width: '8%' }}>
          {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
        </td>
        <td className="tl-td tl-cell-regular" style={{ width: '14%' }}>
          {getCustomerName(payment) || '-'}
        </td>
        <td className="tl-td" style={{ width: '8%' }}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${modeConfig.bg} ${modeConfig.text}`}>
            {modeConfig.label}
          </span>
        </td>
        <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
          {'\u20B9'}{(payment.amount_received || 0).toLocaleString('en-IN')}
        </td>
        <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
          <span className={pendingAmount > 0 ? 'text-amber-600' : ''}>
            {pendingAmount > 0 ? `\u20B9${pendingAmount.toLocaleString('en-IN')}` : '-'}
          </span>
        </td>
        <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
          <span className="truncate">{invoiceRefs || '-'}</span>
        </td>
        <td className="tl-td" style={{ width: '8%' }}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${typeConfig.bg} ${typeConfig.text}`}>
            {typeConfig.label}
          </span>
        </td>
        <td className="tl-td" style={{ width: '7%' }}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
            {statusConfig.label}
          </span>
        </td>
        <td className="tl-td" style={{ width: '5%' }} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onView(payment)}>
                <Eye className="w-4 h-4 mr-2" /> View Details
              </DropdownMenuItem>

              {payment.status === 'draft' && (
                <DropdownMenuItem onClick={() => onEdit(payment)}>
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onClick={() => onDownload(payment.id, getReceiptNo(payment))}>
                <Download className="w-4 h-4 mr-2" /> Download Receipt
              </DropdownMenuItem>

              {payment.status === 'completed' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onVoid(payment)} className="text-red-600 focus:text-red-600">
                    <XCircle className="w-4 h-4 mr-2" /> Void Payment
                  </DropdownMenuItem>
                </>
              )}

              {payment.status === 'draft' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(payment)} className="text-red-600 focus:text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-slate-50/50">
          <td colSpan={11} className="px-6 py-4">
            <div className="grid grid-cols-4 gap-6">
              <div>
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Transaction Details</p>
                <div className="space-y-1 text-[12px]">
                  {(payment.transaction_reference || payment.transaction_id) && (
                    <p><span className="text-slate-500">Reference:</span> <span className="text-slate-800 font-medium">{payment.transaction_reference || payment.transaction_id}</span></p>
                  )}
                  <p><span className="text-slate-500">Deposit To:</span> <span className="text-slate-800">{payment.deposit_to_account || 'Cash'}</span></p>
                  <p><span className="text-slate-500">Collected By:</span> <span className="text-slate-800">{payment.collected_by_name || '-'}</span></p>
                  <p><span className="text-slate-500">Source:</span> <span className="text-slate-800 capitalize">{payment.collection_source || 'manual'}</span></p>
                </div>
              </div>

              <div>
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Allocation Summary</p>
                <div className="space-y-1 text-[12px]">
                  <p><span className="text-slate-500">Amount Received:</span> <span className="text-emerald-600 font-semibold">{'\u20B9'}{(payment.amount_received || 0).toLocaleString('en-IN')}</span></p>
                  <p><span className="text-slate-500">Allocated:</span> <span className="text-slate-800">{'\u20B9'}{(payment.allocated_amount || 0).toLocaleString('en-IN')}</span></p>
                  <p><span className="text-slate-500">Pending:</span> <span className={pendingAmount > 0 ? 'text-amber-600 font-medium' : 'text-slate-400'}>{'\u20B9'}{pendingAmount.toLocaleString('en-IN')}</span></p>
                </div>
              </div>

              <div>
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Invoice Allocations</p>
                {Array.isArray(allocs) && allocs.length > 0 ? (
                  <div className="space-y-1">
                    {allocs.map((alloc, i) => (
                      <div key={i} className="flex items-center justify-between text-[12px]">
                        <span className="text-blue-600 hover:underline cursor-pointer" onClick={() => navigate(`/business/${bid}/sales/invoices/${alloc.invoice_id}`)}>
                          {alloc.invoice_number}
                        </span>
                        <span className="text-slate-800">{'\u20B9'}{(alloc.allocated_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12px] text-amber-600">Advance Payment (No invoice linked)</p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-1">Quick Actions</p>
                <button onClick={() => onView(payment)} className="text-left text-[12px] text-emerald-600 hover:text-emerald-700">{'\u2192'} View Full Details</button>
                {pendingAmount > 0 && payment.status === 'completed' && (
                  <button onClick={() => navigate(`/business/${bid}/payments-received/${payment.id}/allocate`)} className="text-left text-[12px] text-blue-600 hover:text-blue-700">{'\u2192'} Allocate to Invoice</button>
                )}
                <button onClick={() => onDownload(payment.id, getReceiptNo(payment))} className="text-left text-[12px] text-purple-600 hover:text-purple-700">{'\u2192'} Download Receipt</button>
              </div>
            </div>

            {payment.notes && (
              <div className="mt-4 pt-3 border-t border-slate-200">
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-1">Notes</p>
                <p className="text-[12px] text-slate-600">{payment.notes}</p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const PaymentsReceivedPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);

  const [isOwner, setIsOwner] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  const [columnFilters, setColumnFilters] = useState({
    receipt_no: '',
    payment_date: { min: '', max: '' },
    customer_name: '',
    payment_mode: [],
    amount_received: { min: '', max: '' },
    status: [],
    staff_member: ''
  });

  // Void reason for ConfirmModal
  const [voidReason, setVoidReason] = useState('');

  const bid = businessId || activeBusiness?.id;

  const fetchPayments = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (modeFilter !== 'all') params.payment_mode = modeFilter;
      if (searchQuery) params.search = searchQuery;
      if (dateFrom) params.from_date = dateFrom;
      if (dateTo) params.to_date = dateTo;
      if (selectedStaffId) params.staff_id = selectedStaffId;

      const response = await paymentsReceivedAPI.list(params);
      const data = response.data;
      if (data) {
        let filteredPayments = data.payments || [];

        setIsOwner(data.is_owner !== false);
        setStaffList(data.staff_list || []);

        if (paymentTypeFilter !== 'all') {
          filteredPayments = filteredPayments.filter(p => getPaymentType(p) === paymentTypeFilter);
        }
        if (amountMin) {
          filteredPayments = filteredPayments.filter(p => (p.amount_received || 0) >= parseFloat(amountMin));
        }
        if (amountMax) {
          filteredPayments = filteredPayments.filter(p => (p.amount_received || 0) <= parseFloat(amountMax));
        }

        // Column filters
        if (columnFilters.receipt_no) {
          filteredPayments = filteredPayments.filter(p =>
            (getReceiptNo(p)).toLowerCase().includes(columnFilters.receipt_no.toLowerCase())
          );
        }
        if (columnFilters.customer_name) {
          filteredPayments = filteredPayments.filter(p =>
            (getCustomerName(p)).toLowerCase().includes(columnFilters.customer_name.toLowerCase())
          );
        }
        if (columnFilters.staff_member) {
          filteredPayments = filteredPayments.filter(p =>
            (p.created_by_staff_name || 'Owner').toLowerCase().includes(columnFilters.staff_member.toLowerCase())
          );
        }
        if (columnFilters.payment_date?.min || columnFilters.payment_date?.max) {
          filteredPayments = filteredPayments.filter(p => {
            const payDate = new Date(p.payment_date);
            const minOk = !columnFilters.payment_date?.min || payDate >= new Date(columnFilters.payment_date.min);
            const maxOk = !columnFilters.payment_date?.max || payDate <= new Date(columnFilters.payment_date.max);
            return minOk && maxOk;
          });
        }
        if (columnFilters.payment_mode.length > 0) {
          filteredPayments = filteredPayments.filter(p =>
            columnFilters.payment_mode.includes(p.payment_mode)
          );
        }
        if (columnFilters.amount_received?.min || columnFilters.amount_received?.max) {
          filteredPayments = filteredPayments.filter(p => {
            const amt = p.amount_received || 0;
            const minOk = !columnFilters.amount_received?.min || amt >= parseFloat(columnFilters.amount_received.min);
            const maxOk = !columnFilters.amount_received?.max || amt <= parseFloat(columnFilters.amount_received.max);
            return minOk && maxOk;
          });
        }
        if (columnFilters.status.length > 0) {
          filteredPayments = filteredPayments.filter(p =>
            columnFilters.status.includes(p.status)
          );
        }

        setPayments(filteredPayments);
        setStats(data.stats || {});
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load payments'));
    } finally {
      setLoading(false);
    }
  }, [bid, statusFilter, modeFilter, searchQuery, dateFrom, dateTo, paymentTypeFilter, amountMin, amountMax, columnFilters, selectedStaffId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleView = (payment) => navigate(`/business/${bid}/payments-received/${payment.id}`);
  const handleEdit = (payment) => navigate(`/business/${bid}/payments-received/${payment.id}/edit`);

  const handleVoid = (payment) => {
    setVoidReason('');
    setConfirmModal({
      title: 'Void Payment',
      description: `Void receipt ${getReceiptNo(payment)} for \u20B9${(payment.amount_received || 0).toLocaleString('en-IN')}? This will reverse any invoice allocations.`,
      confirmLabel: 'Void Payment',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isVoidFlow: true,
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await paymentsReceivedAPI.void(payment.id, reason);
          toast.success('Payment voided');
          setVoidReason('');
          fetchPayments();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error voiding payment'));
        }
      },
    });
  };

  const handleDelete = (payment) => {
    setConfirmModal({
      title: 'Delete Draft Payment',
      description: `Delete draft receipt ${getReceiptNo(payment)}? This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await paymentsReceivedAPI.delete(payment.id);
          toast.success('Payment deleted');
          fetchPayments();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error deleting payment'));
        }
      },
    });
  };

  const handleDownload = async (id, receiptNo) => {
    try {
      const response = await paymentsReceivedAPI.pdf(id);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt_${receiptNo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error downloading receipt'));
    }
  };

  const hasActiveFilters = statusFilter !== 'all' || modeFilter !== 'all' || paymentTypeFilter !== 'all' ||
                           dateFrom || dateTo || amountMin || amountMax;

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `\u20B9${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `\u20B9${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `\u20B9${(amount / 1000).toFixed(1)}K`;
    return `\u20B9${amount?.toFixed(0) || 0}`;
  };

  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const todayTotal = payments.filter(p => p.payment_date === today && p.status === 'completed')
                             .reduce((sum, p) => sum + (p.amount_received || 0), 0);
  const weekTotal = payments.filter(p => p.payment_date >= weekAgo && p.status === 'completed')
                            .reduce((sum, p) => sum + (p.amount_received || 0), 0);
  const pendingAllocationCount = payments.filter(p => ((p.unallocated_amount || p.excess_amount || 0) > 0) && p.status === 'completed').length;

  return (
    <div className="h-full flex flex-col ">
      <ListPageHeader
        title="Payments Received"
        stats={[
          { icon: DollarSign, label: 'Total Received', value: formatCompactCurrency(stats.total_received || 0), color: 'emerald' },
          { icon: DollarSign, label: 'Today', value: formatCompactCurrency(todayTotal), color: 'blue' },
          { icon: Calendar, label: 'This Week', value: formatCompactCurrency(weekTotal), color: 'violet' },
          { icon: Clock, label: 'Pending', value: pendingAllocationCount, color: 'amber' },
        ]}
        ctaLabel="Record Payment"
        onCtaClick={() => navigate(`/business/${bid}/payments-received/new`)}
      />

      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">{searchQuery || hasActiveFilters ? 'No payments found' : 'No payments recorded yet'}</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">{searchQuery || hasActiveFilters ? 'Try adjusting your filters' : 'Record your first payment'}</p>
              {!searchQuery && !hasActiveFilters && (
                <button onClick={() => navigate(`/business/${bid}/payments-received/new`)} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-[13px] font-medium">
                  Record Payment
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '3%' }}></th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          Receipt #
                          <ColumnFilter type="text" value={columnFilters.receipt_no} onChange={(val) => setColumnFilters({...columnFilters, receipt_no: val})} placeholder="Filter receipt..." />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '8%' }}>
                        <div className="flex items-center gap-1">
                          Payment Date
                          <ColumnFilter type="date" value={columnFilters.payment_date} onChange={(val) => setColumnFilters({...columnFilters, payment_date: val})} />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '14%' }}>
                        <div className="flex items-center gap-1">
                          Customer
                          <ColumnFilter type="text" value={columnFilters.customer_name} onChange={(val) => setColumnFilters({...columnFilters, customer_name: val})} placeholder="Filter customer..." />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '8%' }}>
                        <div className="flex items-center gap-1">
                          Mode
                          <ColumnFilter type="status" value={columnFilters.payment_mode} onChange={(val) => setColumnFilters({...columnFilters, payment_mode: val})} options={[
                            { value: 'cash', label: 'Cash' }, { value: 'upi', label: 'UPI' }, { value: 'bank_transfer', label: 'Bank Transfer' },
                            { value: 'card', label: 'Card' }, { value: 'cheque', label: 'Cheque' }
                          ]} />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal tl-th-right" style={{ width: '10%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Amount Received
                          <ColumnFilter type="number" value={columnFilters.amount_received} onChange={(val) => setColumnFilters({...columnFilters, amount_received: val})} />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal tl-th-right" style={{ width: '10%' }}>Pending</th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>Invoice Ref</th>
                      <th className="tl-th tl-th-teal" style={{ width: '8%' }}>Type</th>
                      <th className="tl-th tl-th-teal" style={{ width: '7%' }}>
                        <div className="flex items-center gap-1">
                          Status
                          <ColumnFilter type="status" value={columnFilters.status} onChange={(val) => setColumnFilters({...columnFilters, status: val})} options={[
                            { value: 'draft', label: 'Draft' }, { value: 'completed', label: 'Completed' },
                            { value: 'voided', label: 'Voided' }, { value: 'refunded', label: 'Refunded' }
                          ]} />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment, index) => (
                      <PaymentRow
                        key={payment.id}
                        payment={payment}
                        index={index}
                        onView={handleView}
                        onEdit={handleEdit}
                        onVoid={handleVoid}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        bid={bid}
                        navigate={navigate}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>Showing <span className="font-medium text-slate-700">{payments.length}</span> payments</p>
                <div className="flex items-center gap-4">
                  <p>Pending: <span className="font-medium text-amber-600">{formatCompactCurrency(stats.total_unallocated || 0)}</span></p>
                  <p>Total: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_received || 0)}</span></p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isVoidFlow ? !voidReason.trim() : false}
        onConfirm={() => confirmModal?.onConfirm?.(voidReason)}
        onClose={() => { setConfirmModal(null); setVoidReason(''); }}
      >
        {confirmModal?.isVoidFlow && (
          <textarea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="e.g. Customer returned goods, duplicate payment, incorrect amount..."
            rows={3}
            className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none"
          />
        )}
      </ConfirmModal>
    </div>
  );
};

export default PaymentsReceivedPage;
